import React from 'react';
import { Alert, Button, Spin, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { MaterialRequestDialog } from '@/components/manufacturing/MaterialRequestDialog';
import { useMaterialRequest } from '@/hooks/useMaterialRequest';

const { Title, Text } = Typography;

const MaterialRequestEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: materialRequest, isLoading, isError, error } = useMaterialRequest(id || '', {
    enabled: !!id,
  });

  const goBack = () => {
    if (id) {
      navigate(`/scm/inventory/material-requests/${id}`);
      return;
    }
    navigate('/scm/inventory/material-requests');
  };

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !materialRequest) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Material request not found"
          description={(error as Error)?.message || 'Unable to load this material request.'}
          action={
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/inventory/material-requests')}>
              Back to list
            </Button>
          }
        />
      </div>
    );
  }

  if (materialRequest.request_status !== 'Draft') {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="warning"
          showIcon
          message="Only draft material requests can be edited"
          description={`${materialRequest.request_number} is ${materialRequest.request_status}.`}
          action={
            <Button type="primary" onClick={goBack}>
              Open request
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={2} style={{ marginBottom: 0 }}>
          Edit Material Request
        </Title>
        <Text type="secondary">{materialRequest.request_number}</Text>
      </div>
      <MaterialRequestDialog
        mode="page"
        materialRequestId={materialRequest.id}
        workOrderId={materialRequest.work_order_id || undefined}
        projectId={materialRequest.project_id || undefined}
        maintenanceCaseId={
          materialRequest.reference_type === 'AssetMaintenanceCase'
            ? materialRequest.reference_id || undefined
            : undefined
        }
        title={`Edit ${materialRequest.request_number}`}
        onBack={goBack}
        onClose={goBack}
        onSuccess={() => navigate(`/scm/inventory/material-requests/${materialRequest.id}`)}
      />
    </div>
  );
};

export default MaterialRequestEditPage;
